import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import AppTableContainer from "@crema/core/AppTableContainer";
import { PlanData } from "types/models/planMaster/planMasterApp";
import TableHeading from "./TableHeading";
import TableItem from "./TableItem";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { AxiosResponse } from "axios";
import {
  createPlanPage,
  deletePlanPage,
  fetchCities as fetchCitiesAction,
  getPlanPageDetail,
} from "redux/actions/planMaster";
import { CityData } from "types/models/master/MasterApp";
import { useDispatch } from "react-redux";

interface PageMappingTableProps {
  pageMapping: PlanData[];
}

const PageMappingTable: React.FC<PageMappingTableProps> = ({
  pageMapping,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [planPositions, setPlanPositions] = useState<number[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<PlanData | null>(null);
  const [cities, setCities] = useState<CityData[]>([]);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("planCode");
  const dispatch = useDispatch();
  const planPositionOptions = Array.from({ length: 10 }, (_, i) => i + 1);

  useEffect(() => {
    console.log("Current states:", { selectedCities, planPositions });
  }, [selectedCities, planPositions]);

  const handleActionClick = async (action: string, plan: PlanData) => {
    setSelectedAction(action);
    setSelectedPlan(plan);
    setOpenDialog(true);

    try {
      const cityResponse = (await dispatch(
        fetchCitiesAction("") as any
      )) as unknown as AxiosResponse;
      console.log("Fetched cities response:", cityResponse);

      let citiesData: CityData[] = [];
      if (cityResponse?.status === 200) {
        citiesData = cityResponse.data.data || [];
        setCities(citiesData);
        console.log("Cities fetched:", citiesData);
      } else {
        console.warn(
          "Failed to fetch cities:",
          cityResponse?.status,
          cityResponse
        );
      }

      const response = (await dispatch(
        getPlanPageDetail(plan.planGroupCode) as any
      )) as unknown as AxiosResponse<any>;
      console.log("Plan page response:", response);

      if (response) {
        const planData = response.data.data || [];
        console.log("Plan data:", planData);

        if (planData.length === 0) {
          console.warn("No plan data returned for planId:", plan.planId);
          setSelectedCities([]);
          setPlanPositions([]);
          return;
        }

        planData.forEach((item: any, index: number) => {
          console.log(`Plan item[${index}]:`, item);
        });

        const selectedCityIds = planData.map((item: any) => item.CityId);
        console.log("Selected City IDs:", selectedCityIds);

        const selectedCityAliases = citiesData
          .filter((city) => selectedCityIds.includes(city.id))
          .map((city) => city.alias);
        console.log("Selected City Aliases:", selectedCityAliases);

        const positions = planData.map(
          (item: any) => item.Position || item.Postion || 1
        );
        console.log("Positions:", positions);

        setSelectedCities(selectedCityAliases);
        setPlanPositions(positions);
      } else {
        console.warn("Failed to fetch plan data:");
        setSelectedCities([]);
        setPlanPositions([]);
      }
    } catch (error) {
      console.error("Error fetching plan data:", error);
      setSelectedCities([]);
      setPlanPositions([]);
    }
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedPageMapping = React.useMemo(() => {
    return [...pageMapping].sort((a, b) => {
      const aValue = a[orderBy as keyof PlanData];
      const bValue = b[orderBy as keyof PlanData];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return order === "asc" ? -1 : 1;
      if (bValue == null) return order === "asc" ? 1 : -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue) * (order === "asc" ? 1 : -1);
      }

      const aNum = parseFloat(String(aValue));
      const bNum = parseFloat(String(bValue));
      const bothAreNumbers = !isNaN(aNum) && !isNaN(bNum);

      if (bothAreNumbers) {
        return (aNum - bNum) * (order === "asc" ? 1 : -1);
      }

      return (
        String(aValue).localeCompare(String(bValue)) *
        (order === "asc" ? 1 : -1)
      );
    });
  }, [pageMapping, order, orderBy]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPlan(null);
    setSelectedCities([]);
    setPlanPositions([]);
  };

  const handleCheckboxChange = (city: string) => {
    setSelectedCities((prev) =>
      prev.includes(city) ? prev.filter((c) => c !== city) : [...prev, city]
    );
  };

  const handleSelectAll = () => {
    const cityAliases = cities.map((city) => city.alias);
    setSelectedCities(
      selectedCities.length === cityAliases.length ? [] : [...cityAliases]
    );
  };

  const handlePositionChange = (index: number, value: number) => {
    const newPositions = [...planPositions];
    newPositions[index] = value;
    setPlanPositions(newPositions);
  };

  const handleSubmit = async () => {
    if (!selectedPlan || !selectedPlan.planId) {
      console.warn("Invalid selected plan or planId");
      return;
    }

    const payload = selectedCities.length
      ? selectedCities.map((alias) => {
          const city = cities.find((c) => c.alias === alias);
          const index = cities.findIndex((c) => c.alias === alias);
          const position = planPositions[index] || 1;

          return {
            PageID: selectedPlan.planId,
            PlanGroupCode: selectedPlan.planGroupCode,
            IsActive: true,
            Postion: position,
            CityId: city?.id,
          };
        })
      : null;

    try {
      if (payload && payload.length > 0) {
        const response = (await dispatch(
          createPlanPage(payload) as any
        )) as AxiosResponse;
        if (response?.status === 200) {
          handleCloseDialog();
        }
      } else {
        const response = (await dispatch(
          deletePlanPage(selectedPlan) as any
        )) as AxiosResponse;
        if (response?.status === 200) {
          handleCloseDialog();
        }
        handleCloseDialog();
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  return (
    <>
      <AppTableContainer>
        <Table stickyHeader className="table">
          <TableHeading order={order} orderBy={orderBy} onSort={handleSort} />
          <TableBody>
            {sortedPageMapping.map((data) => (
              <TableItem
                data={data}
                key={data.planId}
                onEditClick={() => handleActionClick("edit", data)}
              />
            ))}
          </TableBody>
        </Table>
      </AppTableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="font-semibold text-2xl">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span>Assign Cities</span>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleCloseDialog}
              aria-label="close"
              size="large"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <hr className="my-2 border-0 border-t border-gray-400" />

        <DialogContent>
          {selectedPlan && (
            <div>
              <p>
                <strong>Plan Group Code:-</strong> {selectedPlan.planGroupCode}
              </p>
              <p>
                <strong>Plan Name:-</strong> {selectedPlan.planName}
              </p>

              <div>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginTop: "10px",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          padding: "10px",
                          border: "1px solid #ddd",
                          textAlign: "left",
                        }}
                      >
                        Sr.No
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          border: "1px solid #ddd",
                          textAlign: "left",
                        }}
                      >
                        Plan Position
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          border: "1px solid #ddd",
                          textAlign: "left",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCities.length === cities.length}
                            onChange={handleSelectAll}
                          />
                          <strong>Cities</strong>
                        </Box>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {cities.map((city, index) => (
                      <tr key={city.id}>
                        <td
                          style={{ padding: "10px", border: "1px solid #ddd" }}
                        >
                          {index + 1}
                        </td>
                        <td
                          style={{ padding: "10px", border: "1px solid #ddd" }}
                        >
                          <select
                            value={planPositions[index] || 1}
                            onChange={(e) =>
                              handlePositionChange(
                                index,
                                parseInt(e.target.value)
                              )
                            }
                            style={{
                              padding: "5px",
                              borderRadius: "5px",
                              width: "80px",
                              border: "1px solid #ddd",
                            }}
                          >
                            {planPositionOptions.map((num) => (
                              <option key={num} value={num}>
                                {num}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td
                          style={{ padding: "10px", border: "1px solid #ddd" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedCities.includes(city.alias)}
                              onChange={() => handleCheckboxChange(city.alias)}
                            />
                            <label>{city.alias}</label>
                          </Box>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>

        <hr className="my-2 border-0 border-t border-gray-400" />

        <DialogActions>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Button onClick={handleCloseDialog} variant="outlined">
              Close
            </Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Submit
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PageMappingTable;